import { useEffect, useState, useCallback } from "react";
import { Plus, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminShell from "@/components/AdminShell";

export default function AdminCategories() {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState({ name: "", description: "" });
  const [edits, setEdits] = useState({});

  const load = useCallback(() => {
    api.get("/admin/categories").then((r) => setRows(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (creating.name.trim().length < 2) return;
    try {
      await api.post("/admin/categories", creating);
      setCreating({ name: "", description: "" });
      toast.success("Category created");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create category"); }
  };

  const save = async (c) => {
    const patch = edits[c.category_id];
    if (!patch) return;
    await api.patch(`/admin/categories/${c.category_id}`, patch);
    setEdits((s) => { const n = { ...s }; delete n[c.category_id]; return n; });
    toast.success("Saved");
    load();
  };

  const field = (c, key, val) => setEdits((s) => ({ ...s, [c.category_id]: { ...s[c.category_id], [key]: val } }));
  const val = (c, key) => (edits[c.category_id]?.[key] ?? c[key]);

  return (
    <AdminShell active="categories" title="Categories" subtitle="Create, rename and reorder forum categories.">
      <div className="rounded-2xl bg-rose-soft p-5 mb-6" data-testid="cat-create">
        <p className="font-semibold text-plum mb-3">New category</p>
        <div className="flex flex-wrap gap-3">
          <input data-testid="cat-new-name" value={creating.name} onChange={(e) => setCreating({ ...creating, name: e.target.value })} placeholder="Name"
            className="flex-1 min-w-[160px] rounded-full border border-rose/40 px-4 py-2.5 text-sm" />
          <input data-testid="cat-new-desc" value={creating.description} onChange={(e) => setCreating({ ...creating, description: e.target.value })} placeholder="Short description"
            className="flex-[2] min-w-[220px] rounded-full border border-rose/40 px-4 py-2.5 text-sm" />
          <button onClick={create} data-testid="cat-create-btn" className="inline-flex items-center gap-1.5 rounded-full bg-teal px-5 py-2.5 text-sm font-semibold text-white hover:bg-teal-dark"><Plus className="h-4 w-4" /> Add</button>
        </div>
      </div>

      <div className="space-y-3" data-testid="cat-list">
        {rows.map((c) => (
          <div key={c.category_id} className="rounded-2xl bg-white border border-rose/30 p-4" data-testid={`cat-${c.category_id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <input value={val(c, "order")} onChange={(e) => field(c, "order", parseInt(e.target.value) || 0)} type="number" data-testid={`cat-order-${c.category_id}`}
                className="w-16 rounded-lg border border-rose/40 px-2 py-2 text-sm text-center" />
              <input value={val(c, "name")} onChange={(e) => field(c, "name", e.target.value)} data-testid={`cat-name-${c.category_id}`}
                className="min-w-[140px] rounded-lg border border-rose/40 px-3 py-2 text-sm font-medium text-plum" />
              <input value={val(c, "description")} onChange={(e) => field(c, "description", e.target.value)} data-testid={`cat-desc-${c.category_id}`}
                className="flex-1 min-w-[200px] rounded-lg border border-rose/40 px-3 py-2 text-sm" />
              <span className="text-xs text-mutedplum">{c.thread_count} threads</span>
              {edits[c.category_id] && (
                <button onClick={() => save(c)} data-testid={`cat-save-${c.category_id}`} className="inline-flex items-center gap-1.5 rounded-full bg-teal px-4 py-2 text-sm font-semibold text-white"><Save className="h-4 w-4" /> Save</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </AdminShell>
  );
}
